"use client";

import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { Loader as Loader2, ArrowRight } from 'lucide-react';
function LoginForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-8 text-center space-y-4">
        <div className="size-12 rounded-md bg-chart-3/10 flex items-center justify-center mb-2">
          <svg className="size-6 text-chart-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <p className="text-foreground font-medium">{successMessage}</p>
        <p className="text-sm text-muted-foreground">
          Redirecting to your dashboard...
        </p>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-4">
        <FormField control={control} name="email" rules={{
        required: "Email address is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs font-mono uppercase tracking-wider text-muted-foreground">
                Email Address
              </FormLabel>
              <FormControl>
                <Input type="email" placeholder="client@example.com" className="border-x-0 border-t-0 border-b border-border rounded-md bg-transparent px-0 focus-visible:ring-0 focus-visible:border-b-2 focus-visible:border-primary transition-all" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="password" rules={{
        required: "Password is required",
        minLength: {
          value: 8,
          message: "Password must be at least 8 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs font-mono uppercase tracking-wider text-muted-foreground">
                Password
              </FormLabel>
              <FormControl>
                <Input type="password" placeholder="••••••••" className="border-x-0 border-t-0 border-b border-border rounded-md bg-transparent px-0 focus-visible:ring-0 focus-visible:border-b-2 focus-visible:border-primary transition-all" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      {submitError && <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md">
          <p className="text-sm text-destructive">{submitError}</p>
        </div>}

      <div className="pt-2 flex flex-col space-y-4">
        <Button type="submit" disabled={isSubmitting} className="w-full h-12 rounded-md bg-primary text-primary-foreground hover:bg-foreground hover:text-background transition-all duration-300 group">
          {isSubmitting ? <>
              <Loader2 className="mr-2 size-4 animate-spin" />
              Authenticating...
            </> : <>
              Sign In
              <ArrowRight className="ml-2 size-4 group-hover:translate-x-1 transition-transform" />
            </>}
        </Button>

        <div className="text-center">
          <Link to="#" className="text-sm text-muted-foreground hover:text-primary transition-colors inline-flex items-center">
            Forgot your password?
          </Link>
        </div>
      </div>
    </Form>;
}
export default function Login() {
  return <section id="login" className="min-h-[80vh] flex items-center justify-center bg-background py-24 px-6">
      <div className="w-full max-w-md md:max-w-[40%] min-w-[320px]">
        <Card className="border border-border rounded-md bg-card">
          <CardHeader className="space-y-4 pb-8 text-center">
            <CardTitle className="text-2xl md:text-3xl font-default font-semibold flex items-center justify-center">
              <span className="w-1 h-6 bg-primary mr-3 inline-block"></span>
              Client Portal
            </CardTitle>
            <CardDescription className="text-base text-muted-foreground">
              Secure access to your property management and exit strategy dashboard.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <FormProvider formId="client-login-form" sectionName="login" defaultValues={{
            email: "",
            password: ""
          }} submitText="Sign In" successMessage="Authentication successful.">
              <LoginForm />
            </FormProvider>
          </CardContent>
        </Card>
      </div>
    </section>;
}